import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../components/ui/card.tsx";
import { Button } from "../components/ui/button.tsx";
import { Plus, Check } from "lucide-react";
import useDragScroll from "../hooks/useDragScroll.ts";

import NoteEditor, { NoteEditorHandle } from "./NoteEditor.tsx";
import {
  getNotes,
  createNote,
  updateNote,
  deleteNote,
} from "../api/notesApi.ts";

import type { Note } from "../types/note.ts";
import { DEFAULT_COLOR, lightenColor } from "../utils/colors.ts";

import "./NotesPage.css";

export default function NotesPage() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [selected, setSelected] = useState<Note | null>(null);
  const [lastRev, setLastRev] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);
  useDragScroll(listRef);

  const editorRef = useRef<NoteEditorHandle>(null);

  useEffect(() => {
    const load = async () => {
      const fresh = await getNotes(lastRev);
      if (fresh.length) {
        setNotes((prev) => {
          const map = new Map(prev.map((n) => [n.id, n]));
          fresh.forEach((n) =>
            n.deleted ? map.delete(n.id) : map.set(n.id, n)
          );
          return Array.from(map.values()).sort((a, b) => b.rev! - a.rev!);
        });
        setLastRev(Math.max(...fresh.map((n) => n.rev ?? 0), lastRev));
      }
    };
    load();
    const id = setInterval(load, 120_000);
    return () => clearInterval(id);
  }, [lastRev]);

  const cardBg = (argb: number) => lightenColor(argb, 0.5);

  const handleSave = async (note: Note) => {
    if (note.id === 0) {
      const { id, ...rest } = note;
      const created = await createNote(rest);
      setNotes((p) => [created, ...p]);
    } else {
      await updateNote(note);
      setNotes((p) => p.map((n) => (n.id === note.id ? note : n)));
    }
    setSelected(null);
  };

  const handleDelete = async (id: number) => {
    await deleteNote(id);
    setNotes((p) => p.filter((n) => n.id !== id));
    setSelected(null);
  };

  const openNote = (n: Note) => {
    if (selected && editorRef.current?.isDirty()) {
      const answer = window.confirm("Сохранить изменения?");
      if (answer) {
        handleSave(selected);
      }
    }
    setSelected(n);
  };

  return (
    <div className="notes-page">
      <div className="editor-pane">
        {selected ? (
          <NoteEditor
            key={selected.id}
            ref={editorRef}
            note={selected}
            onSave={handleSave}
            onCancel={() => setSelected(null)}
            onDelete={() => handleDelete(selected.id)}
          />
        ) : (
          <div className="empty-hint">Выберите заметку или нажмите «+».</div>
        )}
      </div>

      <div className="list-pane" ref={listRef}>
        <div className="note-grid">
          <Button
            visualStyle="outline"
            className="new-note-btn"
            onClick={() =>
              setSelected({
                  id: 0,
                  title: "",
                  body: "",
                  color: DEFAULT_COLOR,
                  is_list: false,
                  items: [],
                  labels: [],
                  user_id: notes[0]?.user_id ?? 0, // 👈 важно передать user_id
              } as unknown as Note)
            }
          >
            <Plus />
            <span>Новая</span>
          </Button>

          {notes.map((n) => (
            <Card
              key={n.id}
              className="note-card"
              style={{ backgroundColor: cardBg(n.color ?? DEFAULT_COLOR) }}

              onClick={() => openNote(n)}
            >
              <CardContent>
                {n.title && <h3 className="note-title">{n.title}</h3>}
                {!n.is_list ? (
                  <p className="note-body">{n.body}</p>
                ) : (
                 <ul className="note-list">
  {(n.items ?? []).map((it, index) => (
    <li key={index} className={it.checked ? "done" : ""}>
      {it.checked && <Check size={14} />} {it.itemText}
    </li>
  ))}
</ul>

                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}